/**
 * ModuleContext — React context for module configs from backend.
 * Provides `isModuleVisible(key, role)` for sidebar and route protection.
 * Loads from API on mount, falls back to localStorage cache.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { apiFetch } from '../api/client'
import { useAuth } from './AuthContext'
import { isEntitledModuleVisible as isModuleVisiblePure } from './moduleVisibility'

// v3 — bumped 2026-05-08 после восполнения catalog-gap (operating-model,
// spans-of-control, skills-footprint, partners, behaviors,
// quarterly-governance, hr-lifecycle, dialogue-trainer). См.
// backend/app/services/module_seed.py + tests/test_module_seed_completeness.py.
// v2 (2026-05-07) bumped после HH-modules + RBAC permission keys.
// Старые ключи оставались в браузерах сотрудников, которые не делали
// Ctrl+Shift+R после релиза — туда не попадали новые модули, и admin/hr
// не видели их в RBAC до 60 секунд (poll interval). Cache-busting по
// версии гарантирует, что после деплоя клиент сразу подтягивает свежий
// каталог, а не показывает stale data из localStorage.
// v9: добавлены модули `personnel-file` (Личное дело) и `api-docs` (публичная
// справка по REST API) — bump для invalidate stale-cache у клиентов без
// Ctrl+Shift+R после деплоя.
// v10 (PR-F Workflow Engine MVP): добавлены модули `workflow-studio` +
// workflow.* permissions для 7 ролей. Bump чтобы новые пункты меню
// «Бизнес-процессы» и «Мои задачи (процессы)» появились без Ctrl+Shift+R.
// v11 (PR-G UI cleanup): убран дубль модуль `workflow-studio` (он давал
// отдельный пункт меню «Бизнес-процессы»). Workflow Studio existing на
// /workflows стал единственной точкой входа; туда добавлены ссылки на
// /workflow и /workflow/my-tasks (live backend) + кнопка «Добавить раздел».
// v12 (PR-H rework 2026-05-11): задачи Workflow Engine MVP переехали из
// отдельной страницы /workflow/my-tasks в общий /inbox tab «Бизнес-процессы»
// (Workday/Process Street/Asana pattern: cross-process personal task surface).
// Шаблоны в Workflow Studio показаны полным списком с кнопкой «Запустить»
// (только admin/HR/owner/CEO). Bump чтобы новый tab появился в меню Inbox.
// v13 (PR-I 2026-05-11): кнопка «Запустить процесс» переехала на entity:
// CandidateTopNav + EmployeeDetailPage toolbar (Workday/Asana entity-pattern).
// HR думает «этот кандидат нужен onboarding», не «найди template».
// v14 (hotfix 2026-05-11): MUST_HAVE_CORE = ['inbox', 'cabinet'] получает
// приоритет над per-user `extra_modules` для built-in ролей. Иначе admin,
// задавший custom-scope сотруднику с роль assistant/hr/recruiter/...,
// случайно отрезает его от собственного Inbox (где живут task tray
// Workflow Engine MVP с PR-H, v12) и /cabinet (свой профиль). Custom
// admin-defined роли («Завхоз», role='custom') это правило НЕ затрагивает —
// они не в FIXED_ROLES. Инцидент 2026-05-11 (Дынсыма Беликтуевна,
// «Ассистент руководителя» = role='assistant', custom extra_modules без
// inbox → «Доступ ограничен» при открытии Inbox).
// v19 (2026-05-27): добавлен модуль offboarding-handover (режим передачи дел).
// Bump сбрасывает v18-кэш у клиентов — без этого кнопка «Описать свою работу»
// не открывает страницу (isModuleVisible падал в catalog-lookup без этого ключа).
// v20 (2026-07-30): добавлен модуль marketing-channel (страница канала, Волна 2).
// Та же механика: у подрядчика в localStorage лежит v19-снимок каталога без
// нового ключа, catalog-lookup его не находит, и ProtectedRoute рисует «Доступ
// ограничен» — хотя сервер на запрос отвечает 200 и гейт пускает.
// v21 (2026-08-16): добавлен модуль stale-orders («Зависшие заявки»). Та же
// механика, что у marketing-channel: без bump'а в браузере лежит v20-снимок
// каталога без нового ключа, catalog-lookup его не находит, и ProtectedRoute
// рисует «Доступ ограничен» на живом маршруте.
// v22 (срез справочников FIN CORE 2026-08-18): модуль `finance-articles`
// переименован в «Финансовые справочники» и получил роль `payer` в
// allowed_roles. Bump — чтобы подпись пункта меню и доступ плательщика
// приехали сразу, а не после протухания кэша.
// v23: discard the catalog cached before SKU visibility was enforced.
const CACHE_KEY = 'onboardix_module_configs_v23'

try {
    localStorage.removeItem('onboardix_module_configs_v22')
    localStorage.removeItem('onboardix_module_configs')
    localStorage.removeItem('onboardix_module_configs_v21')
    localStorage.removeItem('onboardix_module_configs_v2')
    localStorage.removeItem('onboardix_module_configs_v3')
    localStorage.removeItem('onboardix_module_configs_v4')
    localStorage.removeItem('onboardix_module_configs_v5')
    localStorage.removeItem('onboardix_module_configs_v6')
    localStorage.removeItem('onboardix_module_configs_v7')
    localStorage.removeItem('onboardix_module_configs_v8')
    localStorage.removeItem('onboardix_module_configs_v9')
    localStorage.removeItem('onboardix_module_configs_v10')
    localStorage.removeItem('onboardix_module_configs_v11')
    localStorage.removeItem('onboardix_module_configs_v12')
    localStorage.removeItem('onboardix_module_configs_v13')
    localStorage.removeItem('onboardix_module_configs_v14')
    localStorage.removeItem('onboardix_module_configs_v15')
    localStorage.removeItem('onboardix_module_configs_v16')
    localStorage.removeItem('onboardix_module_configs_v17')
    localStorage.removeItem('onboardix_module_configs_v18')
    localStorage.removeItem('onboardix_module_configs_v19')
    localStorage.removeItem('onboardix_module_configs_v20')
} catch { /* ignore */ }

const ModuleContext = createContext({
    modules: [],
    loading: true,
    refreshing: false,
    isModuleVisible: () => true,
    getModulesByCategory: () => [],
    updateModule: async () => {},
    reload: async () => {},
})

export function useModules() {
    return useContext(ModuleContext)
}

export function ModuleProvider({ children }) {
    const { user } = useAuth()
    const userCacheKey = `${CACHE_KEY}:${user?.organization_id}:${user?.id}`
    const [modules, setModules] = useState(() => {
        try {
            const cached = localStorage.getItem(userCacheKey)
            return cached ? JSON.parse(cached) : []
        } catch { return [] }
    })
    const [loading, setLoading] = useState(modules.length === 0)
    const [refreshing, setRefreshing] = useState(false)
    const [loadError, setLoadError] = useState(false)

    // Не зависеть от modules.length: каждый успешный
    // fetch с новой длиной пересоздавал loadModules, что в свою очередь
    // пересоздавало init- и polling-useEffect ниже → каждый mount Layout
    // (а ModuleProvider оборачивает Layout) получал каскад rerender'ов. Это
    // часть жалобы про «дёрганье экрана» 2026-05-10. setRefreshing
    // показывает «обновление каталога» вместо blocking-loader на повторных
    // pull'ах — UX тот же, без зависимости от modules.length.
    const loadModules = useCallback(async () => {
        setRefreshing(true)
        try {
            const data = await apiFetch('/admin/modules/')
            setModules(data)
            try { localStorage.setItem(userCacheKey, JSON.stringify(data)) } catch { /* Cache is optional. */ }
            setLoadError(false)
        } catch (err) {
            setLoadError(true)
            console.warn('[ModuleContext] Failed to load modules, using cache:', err.message)
        }
        setLoading(false)
        setRefreshing(false)
    }, [userCacheKey])

    useEffect(() => { loadModules() }, [loadModules])

    useEffect(() => {
        const timer = setInterval(loadModules, 60000)
        return () => clearInterval(timer)
    }, [loadModules])

    // На успешный login (AuthContext диспатчит `auth:login`) мгновенно
    // подтягиваем свежий каталог модулей. Иначе HR-пользователь после
    // первого входа в новой сессии до 60 сек видит stale localStorage
    // (без новых HH-пунктов до v11.4). impersonate/stopImpersonating
    // делают `window.location.href`-reload — там ModuleContext
    // перемонтируется и сам fetch'ит свежие данные.
    useEffect(() => {
        const onLogin = () => loadModules()
        window.addEventListener('auth:login', onLogin)
        return () => window.removeEventListener('auth:login', onLogin)
    }, [loadModules])

    // Логика видимости вынесена в pure function `moduleVisibility.js` —
    // покрыта unit-тестами в `tests/unit/moduleVisibility.test.js` (включая
    // regression на инцидент 2026-05-11 «assistant + custom extra_modules
    // без inbox»). Не дублировать логику здесь — менять там.
    const isModuleVisible = useCallback(
        (key, user) => isModuleVisiblePure(key, user, modules),
        [modules],
    )

    const getModulesByCategory = useCallback((category) => {
        return modules.filter(m => m.category === category)
    }, [modules])

    const updateModule = useCallback(async (key, updates) => {
        const updated = await apiFetch(`/admin/modules/${key}`, {
            method: 'PATCH',
            body: JSON.stringify(updates),
        })
        setModules(prev => {
            const next = prev.map(m => m.key === key ? updated : m)
            try { localStorage.setItem(userCacheKey, JSON.stringify(next)) } catch { /* Cache is optional. */ }
            return next
        })
        return updated
    }, [userCacheKey])

    // Forces backend to re-run module_seed.seed_modules() — догоняет в БД
    // модули, которые добавились в module_seed.py после прошлого старта бэка
    // (Railway пересеивает на каждом restart, но ручная кнопка нужна на
    // случай, если bekend упал на startup или admin изменил каталог через
    // деплой и не хочет ждать 60 сек poll).
    const forceReseed = useCallback(async () => {
        const result = await apiFetch('/admin/modules/seed', { method: 'POST' })
        await loadModules()
        return result
    }, [loadModules])

    return (
        <ModuleContext.Provider value={{
            modules,
            loading,
            refreshing,
            isModuleVisible,
            getModulesByCategory,
            updateModule,
            reload: loadModules,
            forceReseed,
        }}>
            {loadError && modules.length === 0 ? <div role="alert" className="flex flex-col items-center justify-center gap-16" style={{ minHeight: '100vh' }}>
                Не удалось загрузить разделы. Проверьте соединение.
                <button type="button" className="btn btn--secondary btn--sm" onClick={loadModules}>Повторить</button>
            </div> : children}
        </ModuleContext.Provider>
    )
}
